import React, { useState, useEffect } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export default function AdminUserManagement() {
    const [users, setUsers] = useState([]);
    const [newUser, setNewUser] = useState({ 
        username: '', 
        firstName: '', 
        lastName: '', 
        email: '', 
        password: '',
        role: ''
    });
    const [sortBy, setSortBy] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setIsLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/getUsers`);
            if (response.ok) {
                const data = await response.json();
                // Filter out users where role is null, undefined, or empty
                const filteredUsers = Array.isArray(data) ? data.filter(user => user.role) : [];
                setUsers(filteredUsers);
            } else {
                console.error('Failed to fetch users');
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setNewUser({ username: '', firstName: '', lastName: '', email: '', password: '', role: '' });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewUser(prev => ({ ...prev, [name]: value }));
    };

    const handleAddOrUpdateUser = async () => {
        if (!newUser.username || !newUser.firstName || !newUser.lastName || !newUser.email || !newUser.role) {
            alert('All fields except password are required for existing users.');
            return;
        }

        if (!newUser.id && !newUser.password) {
            alert('Password is required for new users.');
            return;
        }

        setIsLoading(true);
        try {
            const endpoint = newUser.id 
                ? `${API_BASE_URL}/updateUser` 
                : `${API_BASE_URL}/createUser`;
            
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newUser)
            });

            if (response.ok) {
                fetchUsers();
                closeModal();
                alert(newUser.id ? 'User updated successfully' : 'User created successfully');
            } else {
                const errorData = await response.json();
                alert(`Error: ${errorData.message || 'Failed to save user.'}`);
            }
        } catch (error) {
            console.error('Error saving user:', error);
            alert('An error occurred while saving the user.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteUser = async (userId) => {
        const confirmDelete = window.confirm('Are you sure you want to delete this user?');
        if (!confirmDelete) return;

        setIsLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/deleteUser`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId })
            });

            if (response.ok) {
                setUsers(users.filter(user => user.id !== userId));
            } else {
                const errorData = await response.json();
                alert(`Error: ${errorData.message || 'Failed to delete user.'}`);
            }
        } catch (error) {
            console.error('Error deleting user:', error);
            alert('An error occurred while deleting the user.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditUser = (user) => {
        setNewUser({
            id: user.id, // Store user ID for updates
            username: user.username,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            password: '', // Don't populate password for security
            role: user.role
        });
        openModal();
    };

    const handleToggleAccountStatus = async (userId, currentStatus) => {
        // Toggle to opposite of current status
        const enableAccount = !currentStatus;
        const actionText = enableAccount ? "enable" : "disable";
        
        const confirmToggle = window.confirm(`Are you sure you want to ${actionText} this user account?`);
        if (!confirmToggle) return;

        setIsLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/unlockUser`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, enableAccount })
            });

            if (response.ok) {
                const statusText = enableAccount ? "enabled" : "disabled";
                fetchUsers(); // Refresh user list to update account status
            } else {
                const errorText = await response.text();
                alert(`Error: ${errorText || `Failed to ${actionText} user account.`}`);
            }
        } catch (error) {
            console.error(`Error ${actionText}ing user:`, error);
            alert(`An error occurred while ${actionText}ing the user account.`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSort = (column) => {
        const sortedData = [...users].sort((a, b) => {
            if (a[column] < b[column]) return -1;
            if (a[column] > b[column]) return 1;
            return 0;
        });
        setUsers(sortedData);
        setSortBy(column);
    };

    return (
        <div className="p-1">
            <button className="btn btn-primary mb-1" onClick={openModal}>Add User</button>

            {isLoading ? (
                <div>Loading users...</div>
            ) : users.length > 0 ? (
                <div className="data-table-wrapper">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th onClick={() => handleSort('username')}>Username</th>
                                <th onClick={() => handleSort('firstName')}>First Name</th>
                                <th onClick={() => handleSort('lastName')}>Last Name</th>
                                <th onClick={() => handleSort('email')}>Email</th>
                                <th onClick={() => handleSort('role')}>Role</th>
                                <th onClick={() => handleSort('accountEnabled')}>Account Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(user => (
                                <tr key={user.id}>
                                    <td>{user.username}</td>
                                    <td>{user.firstName}</td>
                                    <td>{user.lastName}</td>
                                    <td>{user.email}</td>
                                    <td>{user.role}</td>
                                    <td>
                                        <span className={user.accountEnabled ? "text-success" : "text-danger"}>
                                            {user.accountEnabled ? "Enabled" : "Disabled"}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="flex gap-1">
                                            <button 
                                                className="btn btn-primary"
                                                onClick={() => handleEditUser(user)}
                                                disabled={isLoading}
                                            >
                                                Edit
                                            </button>
                                            <button 
                                                className={`btn ${user.accountEnabled ? "btn-danger" : "btn-success"}`}
                                                onClick={() => handleToggleAccountStatus(user.id, user.accountEnabled)}
                                                disabled={isLoading}
                                            >
                                                {user.accountEnabled ? "Disable" : "Enable"}
                                            </button>
                                            <button 
                                                className="btn btn-danger"
                                                onClick={() => handleDeleteUser(user.id)}
                                                disabled={isLoading}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div>No users found. Add a user to get started.</div>
            )}

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h3>{newUser.id ? 'Edit User' : 'Add New User'}</h3>
                        <input  
                            className="form-control"
                            type="text"
                            name="username"
                            placeholder="Username (required)"
                            value={newUser.username}
                            onChange={handleInputChange}
                            disabled={!!newUser.id}
                            required
                        />
                        <input
                            className="form-control"
                            type="text"
                            name="firstName"
                            placeholder="First Name (required)"
                            value={newUser.firstName}
                            onChange={handleInputChange}
                            required
                        />
                        <input
                            className="form-control"
                            type="text"
                            name="lastName"
                            placeholder="Last Name (required)"
                            value={newUser.lastName}
                            onChange={handleInputChange}
                            required
                        />
                        <input
                            className="form-control"
                            type="email"
                            name="email"
                            placeholder="Email (required)"
                            value={newUser.email}
                            onChange={handleInputChange}
                            required
                        />
                        <input
                            className="form-control"
                            type="password"
                            name="password"
                            placeholder={newUser.id ? "Password (leave blank to keep current)" : "Password (required)"}
                            value={newUser.password}
                            onChange={handleInputChange}
                            required={!newUser.id}
                        />
                        <select
                            className="form-select"
                            name="role"
                            value={newUser.role}
                            onChange={handleInputChange}
                            required
                        >
                            <option value="">Select Role (required)</option>
                            <option value="admin">Admin</option>
                            <option value="scorekeeper">Scorekeeper</option>
                        </select>
                        <div className="modal-actions">
                            <button className="btn btn-secondary" onClick={closeModal} disabled={isLoading}>
                                Cancel
                            </button>
                            <button className="btn btn-primary" onClick={handleAddOrUpdateUser} disabled={isLoading}>
                                {isLoading ? 'Processing...' : (newUser.id ? 'Update User' : 'Add User')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
